import { useCallback, useMemo } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useSearchPageData } from '#src/app/pages/search/pageContext';
import { useSearchAssetUrl } from '#src/app/pages/search/shared/hooks';

import type { HitProps } from '../../types';

type Result = ModelHookResult<
	{
		createdAtLocalized: string;
		petitionUrl: string;
		imageUrl: string | undefined;
		signaturesCount: number;
		signaturesCountLocalized: string;
		locationName: string | null;
	},
	{
		trackPetitionCardClick: () => void;
	}
>;

export function useHitCard({ hit }: HitProps): Result {
	const { localizeNumber, localizeDate } = useI18n();
	const getAssetUrl = useSearchAssetUrl();
	const track = useTracking();

	const {
		data: { countries },
	} = useSearchPageData();

	const countryDisplayNameMap = useMemo(
		() => Object.fromEntries(countries.map((c) => [c.countryCode, c.displayName])),
		[countries],
	);

	const trackPetitionCardClick = useCallback(() => {
		void track('search_result_petition_card_click');
	}, [track]);

	return {
		data: {
			createdAtLocalized: useMemo(
				// The typesense server returns the created_at in UNIX timestamp value, so we need to multiply by 1000 to get milliseconds
				() => localizeDate(new Date(hit.created_at * 1000), 'date', 'long'),
				[hit.created_at, localizeDate],
			),
			petitionUrl: `/p/${encodeURIComponent(hit.slug)}?source_location=search`,
			imageUrl: useMemo(() => getAssetUrl(hit.photo_url), [getAssetUrl, hit.photo_url]),
			signaturesCount: hit.total_signature_count,
			signaturesCountLocalized: useMemo(
				() => localizeNumber(hit.total_signature_count),
				[hit.total_signature_count, localizeNumber],
			),
			locationName: useMemo(() => {
				const countryName = countryDisplayNameMap[hit.location?.country_code || 'US'];

				if (hit.location?.city && countryName) {
					return `${hit.location.city}, ${countryName}`;
				}
				if (hit.location?.city) {
					return hit.location.city;
				}
				if (countryName) {
					return countryName;
				}
				return null;
			}, [countryDisplayNameMap, hit.location]),
		},
		actions: {
			trackPetitionCardClick,
		},
	};
}
